/**
 * GlobalChat Component
 *
 * AI-powered chat assistant for facility management.
 * Designed to be a global header component across all pages.
 * Features:
 * - Pre-canned questions for quick access
 * - Full conversation history
 * - Dynamic follow-up suggestions
 * - Clickable links for locations and assets
 * - Background brain icon design
 * - Responsive layout
 */

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input, Button, Spin, message } from "antd";
import { SendOutlined, CloseOutlined, ArrowRightOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { GiBrain } from "react-icons/gi";
import {
	sendChatMessage,
	getChatSuggestions,
	type ChatMessage,
} from "@/services/chat-service";
import "./DashboardChat.scss";

// Navigation path labels for user-friendly display
const NAV_LABELS: Record<string, string> = {
	"/": "Dashboard",
	"/locations": "Locations",
	"/forms": "Vision Capture",
	"/asset-scanner": "Asset Scanner",
	"/predictive-spend": "Predictive Spend",
	"/admin": "Administration",
	"/profile": "My Profile",
};

// Follow-up suggestions based on question type
const FOLLOW_UP_SUGGESTIONS: Record<string, string[]> = {
	assets_attention: [
		"Show me more details about these assets",
		"What's the replacement cost for these?",
		"Which locations have the most issues?",
	],
	health_score: [
		"How can I improve the health score?",
		"Which assets are affecting the score?",
		"Show me trending data",
	],
	locations: [
		"What assets are at this location?",
		"Show me the health history",
		"Any recent maintenance?",
	],
	budget: [
		"Break down by asset type",
		"Show me the most expensive items",
		"What's urgent vs can wait?",
	],
	general: [
		"Tell me more",
		"What should I focus on first?",
		"Show me related data",
	],
};

interface GlobalChatProps {
	className?: string;
}

const GlobalChat = ({ className = "" }: GlobalChatProps) => {
	// Navigation
	const navigate = useNavigate();

	// State
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [inputValue, setInputValue] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [suggestions, setSuggestions] = useState<string[]>([]);
	const [isExpanded, setIsExpanded] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
	const [lastQuestionType, setLastQuestionType] = useState<string>("general");

	// Refs
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const overlayInputRef = useRef<HTMLInputElement>(null);

	/**
	 * Parse navigation command from AI response
	 * Returns the path if found, null otherwise
	 */
	const parseNavigationCommand = (content: string): { cleanContent: string; navPath: string | null } => {
		const navRegex = /\[NAV:(\/[^\]]*)\]/;
		const match = content.match(navRegex);
		
		if (match) {
			const navPath = match[1];
			const cleanContent = content.replace(navRegex, "").trim();
			return { cleanContent, navPath };
		}
		
		return { cleanContent: content, navPath: null };
	};

	/**
	 * Handle navigation to a path
	 */
	const handleNavigation = useCallback((path: string) => {
		// Close the overlay first
		setIsClosing(true);
		setTimeout(() => {
			setIsExpanded(false);
			setIsClosing(false);
			setMessages([]);
			setPendingNavigation(null);
			setLastQuestionType("general");
			// Navigate after closing
			navigate(path);
		}, 250);
	}, [navigate]);

	/**
	 * Determine the question type for follow-up suggestions
	 */
	const detectQuestionType = (question: string, response: string): string => {
		const q = question.toLowerCase();
		const r = response.toLowerCase();
		
		if (q.includes("attention") || q.includes("poor") || q.includes("condition") || r.includes("poor condition")) {
			return "assets_attention";
		}
		if (q.includes("health") || q.includes("score") || r.includes("health score")) {
			return "health_score";
		}
		if (q.includes("location") || q.includes("site") || r.includes("location")) {
			return "locations";
		}
		if (q.includes("budget") || q.includes("cost") || q.includes("spend") || r.includes("budget") || r.includes("cost")) {
			return "budget";
		}
		return "general";
	};

	/**
	 * Get dynamic follow-up suggestions
	 */
	const followUpSuggestions = useMemo(() => {
		return FOLLOW_UP_SUGGESTIONS[lastQuestionType] || FOLLOW_UP_SUGGESTIONS.general;
	}, [lastQuestionType]);

	// Scroll to bottom when messages change
	const scrollToBottom = useCallback(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, []);

	useEffect(() => {
		scrollToBottom();
	}, [messages, scrollToBottom]);

	// Fetch suggestions on mount
	useEffect(() => {
		const fetchSuggestions = async () => {
			const suggestedQuestions = await getChatSuggestions();
			setSuggestions(suggestedQuestions);
		};
		fetchSuggestions();
	}, []);

	// Close overlay with animation
	const handleCloseOverlay = useCallback(() => {
		setIsClosing(true);
		setTimeout(() => {
			setIsExpanded(false);
			setIsClosing(false);
			setMessages([]);
			setPendingNavigation(null);
			setLastQuestionType("general");
		}, 250); // Match animation duration
	}, []);

	// Handle sending a message
	const handleSendMessage = async (messageText?: string) => {
		const text = messageText || inputValue.trim();
		if (!text || isLoading) return;

		// Clear any pending navigation
		setPendingNavigation(null);

		// Add user message
		const userMessage: ChatMessage = { role: "user", content: text };
		const newMessages = [...messages, userMessage];
		setMessages(newMessages);
		setInputValue("");
		setIsExpanded(true);
		setIsLoading(true);

		try {
			// Send to API
			const result = await sendChatMessage(newMessages);
			
			// Parse navigation command if present
			const { cleanContent, navPath } = parseNavigationCommand(result.text);
			
			// Store pending navigation
			if (navPath) {
				setPendingNavigation(navPath);
			}
			
			// Detect question type for follow-up suggestions
			const questionType = detectQuestionType(text, cleanContent);
			setLastQuestionType(questionType);
			
			// Add assistant response (with clean content)
			const assistantMessage: ChatMessage = {
				role: "assistant",
				content: cleanContent,
			};
			setMessages([...newMessages, assistantMessage]);
		} catch (error: any) {
			console.error("Chat error:", error);
			message.error(error.message || "Failed to get response. Please try again.");
			// Remove the user message if request failed
			setMessages(messages);
		} finally {
			setIsLoading(false);
		}
	};

	// Handle key press in input
	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage();
		}
	};

	// Handle suggestion click
	const handleSuggestionClick = (suggestion: string) => {
		handleSendMessage(suggestion);
	};

	// Handle follow-up click
	const handleFollowUpClick = (followUp: string) => {
		handleSendMessage(followUp);
	};

	/**
	 * Parse text to find and linkify location IDs and asset references
	 * Looks for patterns like "location 7284" or "at location 7132"
	 */
	const linkifyReferences = (text: string): React.ReactNode[] => {
		const parts: React.ReactNode[] = [];
		
		// Pattern to match "location XXXX" or "at location XXXX"
		const locationPattern = /(?:at\s+)?location\s+(\d+)/gi;
		
		let lastIndex = 0;
		let match;
		
		while ((match = locationPattern.exec(text)) !== null) {
			// Add text before match
			if (match.index > lastIndex) {
				parts.push(text.slice(lastIndex, match.index));
			}
			
			const locationId = match[1];
			const fullMatch = match[0];
			
			// Add linked location
			parts.push(
				<Link
					key={`loc-${match.index}`}
					to={`/locations/${locationId}`}
					className="chat-link location-link"
					onClick={(e) => {
						e.stopPropagation();
						handleCloseOverlay();
					}}
				>
					<EnvironmentOutlined /> {fullMatch}
				</Link>
			);
			
			lastIndex = match.index + fullMatch.length;
		}
		
		// Add remaining text
		if (lastIndex < text.length) {
			parts.push(text.slice(lastIndex));
		}
		
		return parts.length > 0 ? parts : [text];
	};

	// Format message content (handle markdown-like formatting)
	const formatMessage = (content: string) => {
		// Split by newlines and handle bullet points
		return content.split("\n").map((line, i) => {
			const trimmedLine = line.trim();
			
			// Process the line to linkify references
			const processLine = (text: string): React.ReactNode => {
				const linkedContent = linkifyReferences(text);
				return <>{linkedContent}</>;
			};
			
			if (trimmedLine.startsWith("- ") || trimmedLine.startsWith("• ")) {
				return (
					<li key={i} className="chat-list-item">
						{processLine(trimmedLine.substring(2))}
					</li>
				);
			}
			if (trimmedLine.match(/^\d+\.\s/)) {
				return (
					<li key={i} className="chat-list-item">
						{processLine(trimmedLine.replace(/^\d+\.\s/, ""))}
					</li>
				);
			}
			if (trimmedLine.startsWith("**") && trimmedLine.endsWith("**")) {
				return (
					<strong key={i} className="chat-bold">
						{processLine(trimmedLine.slice(2, -2))}
					</strong>
				);
			}
			return (
				<span key={i}>
					{processLine(line)}
					{i < content.split("\n").length - 1 && <br />}
				</span>
			);
		});
	};

	return (
		<div className={`global-chat ${className} ${isExpanded ? "expanded" : ""}`}>
			{/* Background brain icon */}
			<div className="chat-brain-bg">
				<GiBrain />
			</div>

			{/* Input area with suggestions inside */}
			<div className="chat-input-area">
				<div className="chat-input-wrapper">
					{/* Suggestion pills inside the input box */}
					{!isExpanded && !inputValue && suggestions.length > 0 && (
						<div className="chat-inner-suggestions">
							{suggestions.map((suggestion, index) => (
								<button
									key={index}
									type="button"
									className="suggestion-pill"
									onClick={() => handleSuggestionClick(suggestion)}
								>
									{suggestion}
								</button>
							))}
						</div>
					)}
					<Input
						ref={inputRef as any}
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						onKeyPress={handleKeyPress}
						placeholder="Ask about your facilities, equipment, or maintenance..."
						disabled={isLoading}
						className="chat-input"
						suffix={
							<Button
								type="primary"
								icon={<SendOutlined />}
								onClick={() => handleSendMessage()}
								disabled={!inputValue.trim() || isLoading}
								className="send-btn"
							/>
						}
					/>
				</div>
			</div>

			{/* Messages overlay - appears on top of page content */}
			{isExpanded && messages.length > 0 && (
				<>
					{/* Backdrop overlay */}
					<div 
						className={`chat-overlay-backdrop ${isClosing ? "closing" : ""}`} 
						onClick={handleCloseOverlay} 
					/>
					
					{/* Messages panel */}
					<div 
						className={`chat-messages-overlay ${isClosing ? "closing" : ""}`}
						onClick={(e) => e.stopPropagation()}
					>
						{/* Header with close button */}
						<div className="overlay-header">
							<span className="overlay-title">FacilityAI Assistant</span>
							<Button
								type="text"
								icon={<CloseOutlined />}
								onClick={handleCloseOverlay}
								className="chat-clear-btn"
								title="Close conversation"
							/>
						</div>
						
						{/* Messages */}
						<div className="chat-messages">
							{messages.map((msg, index) => (
								<div
									key={index}
									className={`chat-message ${msg.role === "user" ? "user" : "assistant"}`}
								>
									<div className="message-content">
										{formatMessage(msg.content)}
									</div>
								</div>
							))}
							{isLoading && (
								<div className="chat-message assistant">
									<div className="message-content loading">
										<Spin size="small" /> Thinking...
									</div>
								</div>
							)}
							
							{/* Navigation button when AI suggests going somewhere */}
							{pendingNavigation && !isLoading && (
								<div className="chat-navigation">
									<Button
										type="primary"
										icon={<ArrowRightOutlined />}
										onClick={() => handleNavigation(pendingNavigation)}
										className="nav-btn"
									>
										Go to {NAV_LABELS[pendingNavigation] || pendingNavigation}
									</Button>
								</div>
							)}
							
							{/* Follow-up suggestions */}
							{!isLoading && messages.length > 0 && (
								<div className="chat-follow-ups">
									{followUpSuggestions.map((suggestion, index) => (
										<Button
											key={index}
											type="default"
											className="follow-up-btn"
											onClick={() => handleFollowUpClick(suggestion)}
										>
											{suggestion}
										</Button>
									))}
								</div>
							)}
							
							<div ref={messagesEndRef} />
						</div>

						{/* Follow-up input inside overlay */}
						<div className="overlay-input-area">
							<Input
								ref={overlayInputRef as any}
								value={inputValue}
								onChange={(e) => setInputValue(e.target.value)}
								onKeyPress={handleKeyPress}
								placeholder="Ask a follow-up question..."
								disabled={isLoading}
								className="overlay-input"
								suffix={
									<Button
										type="primary"
										icon={<SendOutlined />}
										onClick={() => handleSendMessage()}
										disabled={!inputValue.trim() || isLoading}
										className="send-btn"
									/>
								}
							/>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

// Export both names for backward compatibility
export { GlobalChat, GlobalChat as DashboardChat };
